<script setup lang="ts">
import { ApolloError } from '@apollo/client';
import { computed, nextTick, onMounted, ref } from 'vue';

import { useCommunityAuth } from '@/components/community/utils/composables';
import AddStars from '@/components/ProductReviews/AddStars.vue';
import ProductReview from '@/components/ProductReviews/ProductReview.vue';
import StarRating from '@/components/ProductReviews/StarRating.vue';
import ThankYouModal from '@/components/ProductReviews/ThankYouModal.vue';
import EmptyContent from '@/images/reviews/empty.svg';
import ButtonText from '@/shared/components/UI/Buttons/ButtonText.vue';
import ErrorModal from '@/shared/components/UI/ErrorModal.vue';
import ErrorScreen from '@/shared/components/UI/ErrorScreen.vue';
import IconSpinner from '@/shared/components/UI/Icons/IconSpinner.vue';
import { useProductReviews } from '@/shared/composables/productReviews/useProductReviews';
import { useTranslations } from '@/shared/composables/useTranslations';
import { useProductStore } from '@/shared/pinia/modules/product';
import { getErrorMessage } from '@/shared/utils/error';
import { trackHobbiiEvent } from '@/shared/utils/tracking';

import AddReviewModal from './AddReviewModal.vue';

const { averageRatingScore } = defineProps<{
  averageRatingScore: number;
  reviewCount: number;
}>();

const { t } = useTranslations();
const { profile, initLogin } = useCommunityAuth();
const productStore = useProductStore();
// TODO: once we migrate the product store to TS, remove casting
const productId = computed(
  () => (productStore.legacyId || productStore.productId) as unknown as string
);
const {
  loading,
  reviews,
  error,
  retry,
  retrying,
  loadingMore,
  hasMore,
  loadMore
} = useProductReviews(productId.value);

const showReviewModal = ref(false);
const showThankYouModal = ref(false);
const showErrorModal = ref(false);

const rating = ref(0);
const errorMessage = ref('');
const disableAddStars = ref(false);

const roundedAverageScore = computed(
  () => Math.round(averageRatingScore * 10) / 10
);
const addStars = (e: number) => {
  if (!profile.value) {
    return initLogin();
  }

  showReviewModal.value = true;
  rating.value = e;
};

const reviewSubmitted = (error?: ApolloError | null) => {
  showReviewModal.value = false;
  if (error) {
    errorMessage.value = getErrorMessage(error);
    showErrorModal.value = true;
  } else {
    showThankYouModal.value = true;
    disableAddStars.value = true;
  }
};

const handleAddStars = (e: number) => {
  if (!disableAddStars.value) {
    addStars(e);
  }
};

const loadMoreAnnouncement = ref('');
const handleLoadMore = async () => {
  const previousReviewCount = reviews.value?.length ?? 0;
  loadMoreAnnouncement.value = '';
  await nextTick();
  await loadMore();

  const addedReviewCount =
    (reviews.value?.length ?? previousReviewCount) - previousReviewCount;
  if (addedReviewCount > 0) {
    loadMoreAnnouncement.value = t('reviews.loaded_more', {
      n: addedReviewCount
    });
  }
};

onMounted(() => {
  trackHobbiiEvent('feature_view', {
    category: 'section',
    item_name: 'product_review',
    label: 'Product Review'
  });
});
</script>

<template>
  <div>
    <div
      v-if="loading && !retrying && !loadingMore"
      class="flex h-96 w-full items-center justify-center"
    >
      <IconSpinner class="text-3xl text-primary" />
    </div>

    <div v-else-if="reviews">
      <div
        class="flex w-full flex-col gap-10 pb-10 text-black md:mt-10 md:flex-row md:gap-4"
      >
        <div class="md:basis-72">
          <h2 class="hidden font-bold md:block">
            {{ t('reviews.overall_rating') }}
          </h2>

          <div class="mt-4 flex flex-col items-center gap-3 md:flex-row">
            <div class="text-5xl leading-none">
              {{ roundedAverageScore.toFixed(1) }}
            </div>
            <div class="flex flex-col items-center gap-1 md:items-start">
              <StarRating :rating="averageRatingScore" />
              <div class="mt-2 text-sm leading-none text-black md:mt-0">
                {{ t('reviews.based_on_xx_reviews', { n: reviewCount }) }}
              </div>
            </div>
          </div>
        </div>

        <div class="flex flex-col items-center md:grow md:items-start">
          <h2 class="text-center font-bold md:text-left">
            {{ t('reviews.review_this_product') }}
          </h2>

          <div
            class="mt-2 flex w-full flex-col items-center justify-center md:mt-6 md:items-start"
          >
            <AddStars
              :disabled="disableAddStars"
              :product-id="productId"
              :initial-rating="rating"
              @click="handleAddStars($event)"
            />
          </div>
        </div>
      </div>

      <div class="mb-10 border-t">
        <!-- TODO add filters -->

        <div
          v-if="retrying"
          class="flex h-96 w-full items-center justify-center"
        >
          <IconSpinner class="text-3xl text-primary" />
        </div>
        <div v-else>
          <ErrorScreen
            v-if="error"
            :disabled="retrying"
            :error="t('reviews.error_loading_reviews')"
            :retry="retry"
          />

          <template v-else>
            <div class="grid grid-cols-1 gap-6 divide-y md:gap-8">
              <ProductReview
                v-for="review in reviews"
                :key="`review-${review.id}`"
                :review="review"
              />
            </div>
            <div v-if="hasMore" class="mt-8 text-center">
              <ButtonText
                :loading="loadingMore"
                :keep-focus-while-loading="true"
                :label="t('global.load_more')"
                :loading-label="t('global.loading_more')"
                @click="handleLoadMore"
              />
            </div>
            <p class="sr-only" role="status" aria-live="polite">
              {{ loadMoreAnnouncement }}
            </p>
          </template>
        </div>
      </div>
    </div>

    <div v-else>
      <div
        class="mt-10 flex flex-col items-center pb-12 text-center text-black"
      >
        <h2 class="text-lg font-bold md:text-xl">
          {{ t('reviews.no_reviews_title') }}
        </h2>
        <p class="mt-2 text-sm text-gray-500 md:mt-4">
          {{ t('reviews.no_reviews_description') }}
        </p>
        <div class="my-8 flex flex-col items-center">
          <AddStars
            class="contents"
            :disabled="disableAddStars"
            :product-id="productId"
            :initial-rating="rating"
            @click="handleAddStars($event)"
          />
        </div>
        <img
          :src="EmptyContent"
          width="400"
          height="327"
          loading="lazy"
          alt=""
          class="mt-4 w-full max-w-72"
        />
      </div>
    </div>
  </div>

  <ThankYouModal v-model:active="showThankYouModal" />
  <AddReviewModal
    v-model:active="showReviewModal"
    :product-id="productId"
    :initial-rating="rating"
    @review-submitted="reviewSubmitted"
  />
  <ErrorModal
    v-model:active="showErrorModal"
    :error-message="errorMessage"
    size="md"
  />
</template>
